import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Divider, Tooltip, theme } from "antd";
import { formatAmount } from "../../../../utils/formatAmount.js";
import { __ } from "../../../../utils/i18n.js";
const ChangePlanOrderSummary = ({ selectedPlan, subscription, currency, couponTrigger, couponSlot, onRemoveCoupon })=>{
    const { token } = theme.useToken();
    const renderLabelWithHint = (label, hint)=>/*#__PURE__*/ _jsxs("span", {
            style: {
                display: "inline-flex",
                alignItems: "center",
                gap: 4
            },
            children: [
                label,
                /*#__PURE__*/ _jsx(Tooltip, {
                    title: hint,
                    children: /*#__PURE__*/ _jsx(InfoCircleOutlined, {
                        style: {
                            color: token.colorTextTertiary,
                            fontSize: 12,
                            cursor: "help"
                        }
                    })
                })
            ]
        });
    const isUpgrade = selectedPlan.type === "upgrade";
    const { paymentInterval } = selectedPlan.productPlan;
    // Display values prefer the backend's reverse-charge-aware display fields. For B2C buyers
    // these equal the gross values; for B2B reverse-charge they're the net-of-VAT equivalents
    // so the price row matches what Paddle actually bills.
    const isReverseCharge = !!selectedPlan.isReverseCharge;
    const taxRatePercent = Number(selectedPlan.taxRatePercent ?? 0);
    const newPlanRecurringDisplay = Number(selectedPlan.displayRecurringPrice ?? selectedPlan.productPlan.price[0].recurringPrice);
    const immediatePriceGross = Number(selectedPlan.immediatePrice);
    const couponImmediateDiscount = Number(selectedPlan.discount?.absoluteAmountImmediatePrice ?? 0);
    const couponRecurringDiscountDisplay = Number(selectedPlan.discount?.displayAbsoluteAmountRecurringPrice ?? selectedPlan.discount?.absoluteAmountRecurringPrice ?? 0);
    const recurringAfterCouponDisplay = Number(selectedPlan.discount?.discountedDisplayRecurringPrice ?? selectedPlan.discount?.discountedRecurringPrice ?? newPlanRecurringDisplay);
    const recurringTax = Number(selectedPlan.discount?.discountedRecurringPriceTax ?? selectedPlan.recurringPriceTax ?? 0);
    const discountedImmediatePriceGross = Number(selectedPlan.discount?.discountedImmediatePrice ?? selectedPlan.immediatePrice);
    const immediateTax = Number(selectedPlan.discount?.discountedImmediatePriceTax ?? selectedPlan.immediatePriceTax ?? 0);
    // Plan credit (upgrade): residual value of the current plan, PRE-coupon. Equals
    // `newPlanRecurring − immediatePrice (before any coupon)` so the line surfaces the
    // unused current-plan time as its own positive figure and never absorbs the coupon
    // savings — the coupon gets its own separate row below. Both sides use display
    // values so B2B reverse-charge customers see net-on-net math.
    const planCreditUpgrade = isUpgrade ? Math.max(0, Math.round((newPlanRecurringDisplay - immediatePriceGross) * 100) / 100) : 0;
    const downgradeCredit = Number(selectedPlan.downgradeCreditNextRenewal ?? 0);
    const ratePercent = (gross, tax)=>tax > 0 && gross - tax > 0 ? Math.round(tax / (gross - tax) * 100) : 0;
    const derivedRate = ratePercent(discountedImmediatePriceGross || newPlanRecurringDisplay, immediateTax || recurringTax);
    const taxRate = derivedRate || taxRatePercent;
    const formatPrice = (amount, prefix)=>formatAmount({
            amount,
            currency,
            prefix
        });
    const recurringDisclosure = subscription.nextRenewalDateLabel ? __("Then %s from %s.", formatAmount({
        amount: recurringAfterCouponDisplay,
        currency,
        paymentInterval
    }), subscription.nextRenewalDateLabel) : __("Then %s.", formatAmount({
        amount: recurringAfterCouponDisplay,
        currency,
        paymentInterval
    }));
    const extraRows = [];
    if (isUpgrade && planCreditUpgrade > 0) {
        extraRows.push({
            key: "prorated-credit",
            label: renderLabelWithHint(__("Plan credit"), __("Residual value of your current plan for the time left in your contract period. It's applied to today's payment so you don't pay twice.")),
            amount: formatPrice(planCreditUpgrade, "-"),
            color: token.colorSuccess
        });
    }
    // Coupon row: matches the Checkout V2 label ("Coupon discount") — the applied
    // code itself doesn't repeat here. Inline `×` button removes the coupon when a
    // handler is provided.
    const couponDiscountForToday = couponImmediateDiscount > 0 ? couponImmediateDiscount : couponRecurringDiscountDisplay;
    if (couponDiscountForToday > 0) {
        extraRows.push({
            key: "coupon",
            label: /*#__PURE__*/ _jsxs("span", {
                style: {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4
                },
                children: [
                    __("Coupon discount"),
                    onRemoveCoupon && /*#__PURE__*/ _jsx(Tooltip, {
                        title: __("Remove coupon"),
                        children: /*#__PURE__*/ _jsx(Button, {
                            type: "text",
                            size: "small",
                            icon: /*#__PURE__*/ _jsx(CloseOutlined, {}),
                            onClick: onRemoveCoupon,
                            "aria-label": __("Remove coupon"),
                            style: {
                                padding: 0,
                                width: 20,
                                height: 20,
                                minWidth: 0,
                                color: token.colorSuccess
                            }
                        })
                    })
                ]
            }),
            amount: formatPrice(couponDiscountForToday, "-"),
            color: token.colorSuccess,
            ariaLive: "polite"
        });
    }
    // Downgrade credit sits BELOW "Due today" — it's a forward-looking credit
    // applied to the next renewal invoice, not a deduction from today's charge.
    const postTotalRows = [];
    if (!isUpgrade && downgradeCredit > 0) {
        postTotalRows.push({
            key: "downgrade-credit",
            label: renderLabelWithHint(__("Renewal credit"), __("The unused time on your current plan is credited to your next renewal invoice.")),
            amount: formatPrice(downgradeCredit),
            color: token.colorSuccess
        });
    }
    const taxLabel = isReverseCharge ? __("Reverse charge applied") : taxRate > 0 ? __("Including VAT (%s)", `${taxRate}%`) : __("Including VAT");
    const taxAmount = isReverseCharge ? undefined : isUpgrade ? immediateTax > 0 ? formatPrice(immediateTax) : undefined : recurringTax > 0 ? formatPrice(recurringTax) : undefined;
    const totalAmount = formatPrice(isUpgrade ? discountedImmediatePriceGross : 0);
    const renderRow = (row)=>/*#__PURE__*/ _jsxs("div", {
            role: row.ariaLive ? "status" : undefined,
            "aria-live": row.ariaLive,
            style: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 16,
                marginTop: 8,
                color: row.color ?? token.colorText
            },
            children: [
                /*#__PURE__*/ _jsx("span", {
                    children: row.label
                }),
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        textAlign: "right"
                    },
                    children: row.amount
                })
            ]
        }, row.key);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: "flex",
                    alignItems: "baseline",
                    flexWrap: "wrap",
                    marginBottom: 12,
                    gap: 12
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            fontSize: 16,
                            fontWeight: 600,
                            color: token.colorTextHeading
                        },
                        children: __("Your order")
                    }),
                    couponTrigger && /*#__PURE__*/ _jsx("div", {
                        style: {
                            marginLeft: "auto",
                            whiteSpace: "nowrap"
                        },
                        children: couponTrigger
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 16,
                    color: token.colorTextHeading
                },
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        style: {
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0
                        },
                        children: /*#__PURE__*/ _jsxs("span", {
                            style: {
                                fontWeight: 500
                            },
                            children: [
                                selectedPlan.productPlan.name,
                                ": ",
                                selectedPlan.productPlan.description
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        style: {
                            textAlign: "right"
                        },
                        children: formatAmount({
                            amount: newPlanRecurringDisplay,
                            currency,
                            paymentInterval
                        })
                    })
                ]
            }),
            extraRows.map(renderRow),
            couponSlot && /*#__PURE__*/ _jsx("div", {
                style: {
                    marginTop: 12
                },
                children: couponSlot
            }),
            /*#__PURE__*/ _jsx(Divider, {
                style: {
                    margin: "16px 0"
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 12,
                    color: token.colorTextSecondary,
                    marginBottom: 12
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: taxLabel
                    }),
                    taxAmount !== undefined && /*#__PURE__*/ _jsx("div", {
                        children: taxAmount
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    fontSize: 16,
                    fontWeight: 600,
                    color: token.colorTextHeading
                },
                role: "status",
                "aria-live": "polite",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: __("Due today")
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: totalAmount
                    })
                ]
            }),
            postTotalRows.map(renderRow),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    color: token.colorTextSecondary,
                    fontSize: 12,
                    marginTop: 8,
                    textAlign: "right"
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: recurringDisclosure
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            marginTop: 4,
                            fontStyle: "italic"
                        },
                        children: isUpgrade ? __("Activated immediately. Your contract's renewal date stays the same.") : __("Effective immediately. Your unused time becomes a credit toward your next renewal.")
                    })
                ]
            })
        ]
    });
};
export { ChangePlanOrderSummary };
